import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase-config';
import {
  resolveCompanyDocPath,
  resolveCompanyGeneralSettingsPath,
  resolveCompanyMainSettingsPath,
  resolveCompanyOfficeSettingsPath,
} from './core/company/companyPaths';
import { geocodeAddress } from './modules/logistics/components/LogisticsUtils';

const Icon = ({ path, className = 'w-6 h-6', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const createEmptyForm = (fallbackCompanyName = '') => ({
  companyName: fallbackCompanyName || '',
  responsibleName: '',
  contactEmail: '',
  contactPhone: '',
  officeName: fallbackCompanyName || '',
  street: '',
  streetNumber: '',
  floor: '',
  apartment: '',
  locality: '',
  province: '',
  postalCode: '',
  country: 'Argentina',
  fullAddress: '',
  reference: '',
  lat: '',
  lng: '',
});

const composeFullAddress = (values = {}) => {
  const streetLine = [values.street, values.streetNumber].filter(Boolean).join(' ').trim();
  return [
    streetLine,
    values.locality,
    values.province,
    values.country,
    values.postalCode,
  ]
    .filter(Boolean)
    .join(', ')
    .trim();
};

const normalizeInputValue = (value) => String(value || '').trim();

const getSafeLatLngValue = (value) => {
  const trimmed = normalizeInputValue(value);
  if (!trimmed) return null;
  const numeric = Number(trimmed.replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : null;
};

const getOfficeShortName = (officeName = '', companyName = '') => {
  const base = normalizeInputValue(officeName) || normalizeInputValue(companyName) || 'Base';
  const words = base.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
};

const GEO_STATUS_STYLES = {
  idle: 'border-slate-700/70 bg-slate-900 text-slate-300',
  stale: 'border-amber-400/30 bg-amber-500/10 text-amber-100',
  validating: 'border-sky-400/30 bg-sky-500/10 text-sky-100',
  success: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100',
  error: 'border-rose-400/30 bg-rose-500/10 text-rose-100',
  manual: 'border-cyan-400/30 bg-cyan-500/10 text-cyan-100',
};

const GEO_STATUS_META = {
  idle: {
    label: 'Sin validar',
    title: 'La oficina central todavía no fue validada',
  },
  stale: {
    label: 'Pendiente',
    title: 'La dirección cambió y necesita nueva validación',
  },
  validating: {
    label: 'Validando',
    title: 'Estamos comprobando la dirección en el mapa',
  },
  success: {
    label: 'Validada',
    title: 'La oficina central quedó fijada correctamente',
  },
  error: {
    label: 'Error',
    title: 'La dirección no pudo validarse todavía',
  },
  manual: {
    label: 'Manual',
    title: 'Hay coordenadas manuales listas para guardar',
  },
};

const FormField = ({ label, value, onChange, placeholder, type = 'text', required = false, readOnly = false }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
      {label}
      {required ? ' *' : ''}
    </span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      className={`w-full rounded-2xl border border-slate-700 bg-slate-900/90 px-4 py-3 text-sm text-white outline-none transition-all placeholder:text-slate-500 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20 ${readOnly ? 'opacity-80' : ''}`}
    />
  </label>
);

const TextAreaField = ({ label, value, onChange, placeholder, rows = 3, required = false, hint = '' }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
      {label}
      {required ? ' *' : ''}
    </span>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className="w-full resize-none rounded-2xl border border-slate-700 bg-slate-900/90 px-4 py-3 text-sm text-white outline-none transition-all placeholder:text-slate-500 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20"
    />
    {hint ? <span className="mt-2 block text-xs leading-relaxed text-slate-500">{hint}</span> : null}
  </label>
);

function CompanySettingsModal({
  show,
  onClose,
  companyId,
  companyName,
  showMessage,
}) {
  const [form, setForm] = useState(() => createEmptyForm(companyName));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [geoStatus, setGeoStatus] = useState('idle');
  const [geoMessage, setGeoMessage] = useState('Validá la base operativa para ubicar la oficina central en el mapa.');

  const composedAddressPreview = useMemo(() => composeFullAddress(form), [form]);
  const validatedAddress = useMemo(() => normalizeInputValue(form.fullAddress), [form.fullAddress]);
  const effectiveGeoStatus = isValidatingAddress ? 'validating' : geoStatus;
  const effectiveGeoMeta = GEO_STATUS_META[effectiveGeoStatus] || GEO_STATUS_META.idle;

  useEffect(() => {
    if (!show || !db || !companyId) return undefined;

    let isMounted = true;
    setIsLoading(true);

    const loadData = async () => {
      try {
        const generalRef = doc(db, resolveCompanyGeneralSettingsPath(companyId));
        const officeRef = doc(db, resolveCompanyOfficeSettingsPath(companyId));
        const [generalSnap, officeSnap] = await Promise.all([getDoc(generalRef), getDoc(officeRef)]);

        if (!isMounted) return;

        const generalData = generalSnap.data() || {};
        const officeData = officeSnap.data() || {};
        const hasCoords = officeData.lat === 0 || officeData.lat || officeData.lng === 0 || officeData.lng;
        const initialAddress = officeData.fullAddress || officeData.address || composeFullAddress(officeData);

        setForm({
          companyName: generalData.companyName || companyName || '',
          responsibleName: generalData.responsibleName || '',
          contactEmail: generalData.contactEmail || '',
          contactPhone: generalData.contactPhone || '',
          officeName: officeData.officeName || officeData.name || generalData.companyName || companyName || '',
          street: officeData.street || '',
          streetNumber: officeData.streetNumber || '',
          floor: officeData.floor || '',
          apartment: officeData.apartment || '',
          locality: officeData.locality || '',
          province: officeData.province || '',
          postalCode: officeData.postalCode || '',
          country: officeData.country || 'Argentina',
          fullAddress: initialAddress,
          reference: officeData.reference || '',
          lat: officeData.lat === 0 || officeData.lat ? String(officeData.lat) : '',
          lng: officeData.lng === 0 || officeData.lng ? String(officeData.lng) : '',
        });

        if (hasCoords && initialAddress) {
          setGeoStatus('success');
          setGeoMessage(`Oficina central lista en mapa: ${initialAddress}`);
        } else {
          setGeoStatus('idle');
          setGeoMessage('Validá la base operativa para ubicar la oficina central en el mapa.');
        }
      } catch (error) {
        console.error('Error cargando configuración de empresa:', error);
        showMessage?.('No se pudo cargar la configuración de empresa.', 'error');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [companyId, companyName, show, showMessage]);

  const updateField = (field) => (event) => {
    const nextValue = event.target.value;
    setForm((current) => ({
      ...current,
      [field]: nextValue,
    }));

    if (field === 'fullAddress') {
      setGeoStatus('stale');
      setGeoMessage('La dirección completa cambió. Volvé a validarla para actualizar la oficina central del mapa.');
    }

    if (field === 'lat' || field === 'lng') {
      setGeoStatus('manual');
      setGeoMessage('Coordenadas cargadas manualmente. Guardá para fijar la oficina central en esta ubicación.');
    }
  };

  const validateAddress = async ({ silent = false } = {}) => {
    const addressToValidate = validatedAddress;

    if (!addressToValidate) {
      if (!silent) {
        showMessage?.('Completá el campo Dirección completa antes de validarla en mapa.', 'warning');
      }
      setGeoStatus('error');
      setGeoMessage('Falta completar Dirección completa para validar la oficina central.');
      return null;
    }

    setIsValidatingAddress(true);
    try {
      const coords = await geocodeAddress(addressToValidate, silent ? null : showMessage);
      if (coords?.lat !== undefined && coords?.lng !== undefined) {
        setForm((current) => ({
          ...current,
          lat: String(coords.lat),
          lng: String(coords.lng),
          fullAddress: current.fullAddress || addressToValidate,
        }));
        setGeoStatus('success');
        setGeoMessage(`Mapa validado. La oficina central quedó asociada a: ${addressToValidate}`);
        if (!silent) {
          showMessage?.('Dirección validada. La oficina central ya tiene coordenadas.', 'success');
        }
        return coords;
      }

      setGeoStatus('error');
      setGeoMessage('No se pudo ubicar la dirección en el mapa. Ajustala y probá de nuevo.');
      return null;
    } catch (error) {
      console.error('Error validando dirección de empresa:', error);
      setGeoStatus('error');
      setGeoMessage('Hubo un problema validando la dirección en el mapa.');
      if (!silent) {
        showMessage?.('No se pudo validar la dirección operativa.', 'error');
      }
      return null;
    } finally {
      setIsValidatingAddress(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!db || !companyId) return;

    const nextCompanyName = normalizeInputValue(form.companyName);
    const nextResponsibleName = normalizeInputValue(form.responsibleName);
    const nextContactEmail = normalizeInputValue(form.contactEmail);
    const nextOfficeName = normalizeInputValue(form.officeName);
    const nextLocality = normalizeInputValue(form.locality);
    const nextProvince = normalizeInputValue(form.province);
    const nextCountry = normalizeInputValue(form.country);

    if (!nextCompanyName || !nextResponsibleName || !nextContactEmail || !nextOfficeName || !nextLocality || !nextProvince || !nextCountry) {
      showMessage?.('Completá los campos principales de identidad y base operativa.', 'error');
      return;
    }

    if (!validatedAddress) {
      showMessage?.('Dirección completa es obligatoria para validar la oficina central en mapa.', 'error');
      setGeoStatus('error');
      setGeoMessage('Completá Dirección completa para poder validar y guardar la oficina central.');
      return;
    }

    setIsSaving(true);

    try {
      const finalAddress = validatedAddress;
      let finalLat = getSafeLatLngValue(form.lat);
      let finalLng = getSafeLatLngValue(form.lng);

      if (finalAddress && (!Number.isFinite(finalLat) || !Number.isFinite(finalLng) || geoStatus === 'stale' || geoStatus === 'idle' || geoStatus === 'error')) {
        const validatedCoords = await validateAddress({ silent: true });
        if (!validatedCoords) {
          showMessage?.('La dirección no pudo validarse en mapa. Revisala antes de guardar.', 'error');
          return;
        }
        finalLat = Number(validatedCoords.lat);
        finalLng = Number(validatedCoords.lng);
      }

      const officeShortName = getOfficeShortName(nextOfficeName, nextCompanyName);
      const contactPhone = normalizeInputValue(form.contactPhone);
      const postalCode = normalizeInputValue(form.postalCode);
      const floor = normalizeInputValue(form.floor);
      const apartment = normalizeInputValue(form.apartment);
      const reference = normalizeInputValue(form.reference);
      const street = normalizeInputValue(form.street);
      const streetNumber = normalizeInputValue(form.streetNumber);

      const generalPayload = {
        companyId,
        companyName: nextCompanyName,
        responsibleName: nextResponsibleName,
        contactEmail: nextContactEmail,
        contactPhone,
        updatedAt: serverTimestamp(),
      };

      const officePayload = {
        companyId,
        officeName: nextOfficeName,
        name: nextOfficeName,
        shortName: officeShortName,
        street,
        streetNumber,
        floor,
        apartment,
        locality: nextLocality,
        province: nextProvince,
        postalCode,
        country: nextCountry,
        fullAddress: finalAddress,
        address: finalAddress,
        reference,
        lat: finalLat,
        lng: finalLng,
        geocodedAt: serverTimestamp(),
        geocodingStatus: Number.isFinite(finalLat) && Number.isFinite(finalLng) ? 'validated' : 'pending',
        timezone: 'America/Argentina/Buenos_Aires',
        updatedAt: serverTimestamp(),
      };

      const mainPayload = {
        companyId,
        companyName: nextCompanyName,
        officeLocation: {
          name: nextOfficeName,
          shortName: officeShortName,
          address: finalAddress,
          lat: finalLat,
          lng: finalLng,
          reference,
        },
        updatedAt: serverTimestamp(),
      };

      const rootPayload = {
        companyId,
        companyName: nextCompanyName,
        responsibleName: nextResponsibleName,
        contactEmail: nextContactEmail,
        contactPhone,
        officeName: nextOfficeName,
        locality: nextLocality,
        province: nextProvince,
        country: nextCountry,
        fullAddress: finalAddress,
        lat: finalLat,
        lng: finalLng,
        updatedAt: serverTimestamp(),
      };

      await Promise.all([
        setDoc(doc(db, resolveCompanyGeneralSettingsPath(companyId)), generalPayload, { merge: true }),
        setDoc(doc(db, resolveCompanyOfficeSettingsPath(companyId)), officePayload, { merge: true }),
        setDoc(doc(db, resolveCompanyMainSettingsPath(companyId)), mainPayload, { merge: true }),
        setDoc(doc(db, resolveCompanyDocPath(companyId)), rootPayload, { merge: true }),
      ]);

      setGeoStatus('success');
      setGeoMessage(`Oficina central actualizada en mapa: ${finalAddress}`);
      showMessage?.('Configuración de empresa actualizada y oficina central validada.', 'success');
      onClose?.();
    } catch (error) {
      console.error('Error guardando configuración de empresa:', error);
      showMessage?.('No se pudo guardar la configuración de empresa.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm">
      <div className="relative max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[28px] border border-slate-800 bg-slate-950 text-white shadow-[0_30px_80px_rgba(2,6,23,0.65)]">
        <div className="border-b border-slate-800 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.16),transparent_42%),linear-gradient(180deg,rgba(15,23,42,0.98),rgba(2,6,23,0.96))] px-6 py-5 md:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-100">
                <Icon path="M3.75 4.5h16.5M3.75 9h16.5m-16.5 4.5h9.75m-9.75 4.5h16.5" className="h-4 w-4" />
                Configuración de empresa
              </div>
              <h2 className="mt-4 text-2xl font-black tracking-[-0.03em] text-white md:text-3xl">Identidad operativa y base territorial</h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-300">
                Definí el núcleo operativo de {companyName || 'tu empresa'} para que la plataforma lea mejor el contexto real de trabajo.
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-300">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Empresa activa</p>
              <p className="mt-1 font-semibold text-white">{companyName || companyId}</p>
              <p className="mt-2 text-xs text-slate-400">ID: {companyId}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="max-h-[calc(92vh-130px)] overflow-y-auto px-6 py-6 md:px-8 md:py-7">
          {isLoading ? (
            <div className="rounded-[24px] border border-slate-800 bg-slate-900/70 px-5 py-10 text-center text-slate-300">
              Cargando configuración de empresa...
            </div>
          ) : (
            <div className="space-y-5">
              <section className="rounded-[24px] border border-slate-800 bg-slate-900/75 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] md:p-6">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-500/10 text-cyan-100">
                    <Icon path="M4.5 12.75l6 6 9-13.5" className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Identidad</p>
                    <h3 className="text-lg font-bold text-white">Datos principales de la empresa</h3>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <FormField label="Nombre de empresa" value={form.companyName} onChange={updateField('companyName')} placeholder="G-CENTRA Empresa" required />
                  <FormField label="Responsable" value={form.responsibleName} onChange={updateField('responsibleName')} placeholder="Nombre y apellido" required />
                  <FormField label="Email" value={form.contactEmail} onChange={updateField('contactEmail')} placeholder="operaciones@empresa.com" type="email" required />
                  <FormField label="Teléfono" value={form.contactPhone} onChange={updateField('contactPhone')} placeholder="+54 11 ..." />
                </div>
              </section>

              <section className="rounded-[24px] border border-slate-800 bg-slate-900/75 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] md:p-6">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-emerald-400/20 bg-emerald-500/10 text-emerald-100">
                    <Icon path="M12 21a8.97 8.97 0 006.364-2.636A9 9 0 1012 21zm0-13.5a4.5 4.5 0 110 9 4.5 4.5 0 010-9z" className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Base operativa</p>
                    <h3 className="text-lg font-bold text-white">Ubicación central de la operación</h3>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <FormField label="Base operativa" value={form.officeName} onChange={updateField('officeName')} placeholder="Centro logístico" required />
                  <FormField label="Calle" value={form.street} onChange={updateField('street')} placeholder="Av. Siempre Viva" />
                  <FormField label="Número" value={form.streetNumber} onChange={updateField('streetNumber')} placeholder="742" />
                  <FormField label="Piso / sector" value={form.floor} onChange={updateField('floor')} placeholder="Piso 2" />
                  <FormField label="Departamento / unidad" value={form.apartment} onChange={updateField('apartment')} placeholder="Oficina A" />
                  <FormField label="Localidad" value={form.locality} onChange={updateField('locality')} placeholder="Lomas de Zamora" required />
                  <FormField label="Provincia" value={form.province} onChange={updateField('province')} placeholder="Buenos Aires" required />
                  <FormField label="Código postal" value={form.postalCode} onChange={updateField('postalCode')} placeholder="1832" />
                  <FormField label="País" value={form.country} onChange={updateField('country')} placeholder="Argentina" required />
                  <div className="md:col-span-2 xl:col-span-3">
                    <TextAreaField label="Dirección completa" value={form.fullAddress} onChange={updateField('fullAddress')} placeholder={composedAddressPreview || 'Ingresá la dirección exacta de la oficina central.'} rows={3} required hint="Este es el campo obligatorio que se usa para validar la oficina central en el mapa." />
                  </div>
                  <TextAreaField label="Referencia operativa" value={form.reference} onChange={updateField('reference')} placeholder="Portón lateral, ingreso sobre colectora, etc." rows={3} />
                </div>
              </section>

              <section className="rounded-[24px] border border-slate-800 bg-slate-900/75 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] md:p-6">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-violet-400/20 bg-violet-500/10 text-violet-100">
                    <Icon path="M9 20.25h6M12 3v17.25m8.25-8.25H3.75" className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Georreferencia</p>
                    <h3 className="text-lg font-bold text-white">Validación real de oficina central</h3>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <FormField label="Latitud" value={form.lat} onChange={updateField('lat')} placeholder="-34.7606" />
                  <FormField label="Longitud" value={form.lng} onChange={updateField('lng')} placeholder="-58.4070" />
                  <div className="md:col-span-2 xl:col-span-2">
                    <label className="block">
                      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Dirección operativa validada</span>
                      <div className="rounded-2xl border border-slate-800 bg-slate-950/80 px-4 py-3 text-sm text-slate-300">
                        {validatedAddress || 'Todavía no hay una dirección completa lista para validar.'}
                      </div>
                    </label>
                  </div>
                </div>

                <div className="mt-4 space-y-3 rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className={`rounded-2xl border px-4 py-3 text-sm ${GEO_STATUS_STYLES[effectiveGeoStatus] || GEO_STATUS_STYLES.idle}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-80">Estado de oficina central</p>
                        <span className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white">
                          {effectiveGeoMeta.label}
                        </span>
                      </div>
                      <p className="mt-2 font-semibold">{effectiveGeoMeta.title}</p>
                      <p className="mt-1 text-sm opacity-90">{geoMessage}</p>
                    </div>

                    <button
                      type="button"
                      onClick={() => validateAddress()}
                      disabled={isValidatingAddress || isLoading}
                      className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-5 text-sm font-semibold text-emerald-50 transition-colors hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Icon path="M12 3c2.755 0 5.455 1.12 7.182 2.818M12 3C9.245 3 6.545 4.12 4.818 5.818M12 3v18m0 0c2.755 0 5.455-1.12 7.182-2.818M12 21c-2.755 0-5.455-1.12-7.182-2.818" className="h-4 w-4" />
                      {isValidatingAddress ? 'Validando mapa...' : 'Validar dirección en mapa'}
                    </button>
                  </div>

                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Resultado</p>
                      <p className="mt-1 text-sm font-semibold text-white">{effectiveGeoMeta.label}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Latitud</p>
                      <p className="mt-1 text-sm font-semibold text-white">{form.lat || 'Sin coordenada'}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/80 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Longitud</p>
                      <p className="mt-1 text-sm font-semibold text-white">{form.lng || 'Sin coordenada'}</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-slate-800 pt-5 md:flex-row md:items-center md:justify-between">
            <p className="text-xs text-slate-500">
              La oficina central se valida únicamente con el campo Dirección completa y solo se guarda cuando queda asociada a coordenadas reales.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex min-h-[46px] items-center justify-center rounded-2xl border border-slate-700 bg-slate-900 px-5 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving || isLoading || isValidatingAddress}
                className="inline-flex min-h-[46px] items-center justify-center rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-5 text-sm font-semibold text-cyan-50 transition-colors hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? 'Guardando...' : 'Guardar configuración'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompanySettingsModal;
